import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  CreateOrganizationInput,
  MagicLinkInput,
  SignInInput,
  SignUpInput,
} from '@church/domain'
import { useAuthClient } from '../auth-context.tsx'

/**
 * Auth flows as mutations. Each one funnels better-auth's `{ data, error }`
 * result into throw-on-error so react-hook-form and mutation state see
 * failures, and resets the query cache on success — a sign-in/out invalidates
 * literally everything on screen.
 */

interface AuthResult {
  error?: { message?: string; status?: number } | null
}

/**
 * The failure keeps its HTTP status and reads like an API problem (`value.
 * detail`), so classifyError tells "wrong password" (401) from "couldn't
 * reach the server" — a bare Error would read as the second.
 */
function unwrap<T extends AuthResult>(result: T, fallback: string): T {
  if (result.error) {
    const detail = result.error.message ?? fallback
    // No status: the request never got an answer (offline).
    if (!result.error.status) throw new TypeError(detail)
    throw Object.assign(new Error(detail), {
      status: result.error.status,
      value: { title: detail, detail },
    })
  }
  return result
}

/**
 * After an identity change everything on screen is stale. `resetQueries` (not
 * `clear`) — clearing empties the cache but does NOT refetch active queries,
 * so screens keep rendering the signed-out snapshot until a remount; reset
 * refetches every active query against the new session.
 */

export function useSignIn() {
  const authClient = useAuthClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: SignInInput) =>
      unwrap(
        await authClient.signIn.email({ email: input.email, password: input.password }),
        'Sign-in failed',
      ),
    onSuccess: () => queryClient.resetQueries(),
  })
}

export function useSignUp() {
  const authClient = useAuthClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: SignUpInput) =>
      unwrap(
        await authClient.signUp.email({
          name: input.name,
          email: input.email,
          password: input.password,
        }),
        'Sign-up failed',
      ),
    onSuccess: () => queryClient.resetQueries(),
  })
}

export function useSignOut() {
  const authClient = useAuthClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => unwrap(await authClient.signOut(), 'Sign-out failed'),
    // Even a failed sign-out drops local state: the user asked to leave.
    onSettled: () => queryClient.resetQueries(),
  })
}

export function useSendMagicLink() {
  const authClient = useAuthClient()
  return useMutation({
    mutationFn: async (input: MagicLinkInput & { callbackURL?: string }) =>
      unwrap(
        await authClient.signIn.magicLink({
          email: input.email,
          callbackURL: input.callbackURL,
        }),
        'Could not send the sign-in link',
      ),
  })
}

export function useForgotPassword() {
  const authClient = useAuthClient()
  return useMutation({
    mutationFn: async (input: { email: string; redirectTo: string }) =>
      unwrap(
        await authClient.requestPasswordReset({
          email: input.email,
          redirectTo: input.redirectTo,
        }),
        'Could not send the reset email',
      ),
  })
}

export function useResetPassword() {
  const authClient = useAuthClient()
  return useMutation({
    mutationFn: async (input: { newPassword: string; token: string }) =>
      unwrap(await authClient.resetPassword(input), 'Could not reset the password'),
  })
}

/** Accepts an invitation and lands the user in the inviting org. */
export function useAcceptInvitation() {
  const authClient = useAuthClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const accepted = unwrap(
        await authClient.organization.acceptInvitation({ invitationId }),
        'Could not accept the invitation — it may have expired',
      )
      const orgId = accepted.data?.invitation.organizationId
      if (orgId) {
        unwrap(
          await authClient.organization.setActive({ organizationId: orgId }),
          'Joined, but could not switch to the organization',
        )
      }
      return accepted
    },
    onSuccess: () => queryClient.resetQueries(),
  })
}

/** Creates a church and makes it the active org (creator becomes `owner`). */
export function useCreateOrganization() {
  const authClient = useAuthClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateOrganizationInput) => {
      const created = unwrap(
        await authClient.organization.create({ name: input.name, slug: input.slug }),
        'Could not create the organization',
      )
      const orgId = created.data?.id
      if (orgId) {
        unwrap(
          await authClient.organization.setActive({ organizationId: orgId }),
          'Created, but could not switch to the new organization',
        )
      }
      return created
    },
    onSuccess: () => queryClient.resetQueries(),
  })
}

/** Sends the verify-your-email link again, to the signed-in account's address. */
export function useResendVerification() {
  const authClient = useAuthClient()
  return useMutation({
    mutationFn: async (input: { email: string; callbackURL?: string }) =>
      unwrap(
        await authClient.sendVerificationEmail({
          email: input.email,
          callbackURL: input.callbackURL,
        }),
        'Could not send the verification email',
      ),
  })
}
